import type { AxiosError } from "axios";
import type { TlxHttpResponse } from "@/api";

export interface ApiErrorDetail {
  /** The HTTP status, or undefined when no response arrived. */
  status?: number;
  /** The envelope's own message, as the server wrote it. */
  message?: string;
  /**
   * A stable error code: the envelope's `data` when it is a string, or a string
   * `code` in the body, as a coded conflict sends (`{ code: "SCOPE_CHANGED", message }`).
   * The envelope's own `code` is the numeric status, so it never counts.
   */
  code?: string;
  /** The envelope's `data`, when it is an object (e.g. `{ registryId }`). */
  payload?: Record<string, unknown>;
}

/**
 * What a rejected API call says about itself, read from the `TlxHttpResponse`
 * envelope: `{ code, message, data }`, where `data` is either a stable error
 * code (`"SECRET_REQUIRED"`) or an object carrying what survived the failure.
 * Never throws - an error with no response yields only what it has.
 */
export const apiErrorDetail = (error: unknown): ApiErrorDetail => {
  const response = (error as AxiosError<TlxHttpResponse> | undefined)?.response;
  const body = response?.data;
  const data = body?.data as unknown;
  return {
    status: response?.status,
    message: typeof body?.message === "string" ? body.message : undefined,
    code: [data, body?.code].find(
      (value): value is string => typeof value === "string",
    ),
    payload:
      data && typeof data === "object" && !Array.isArray(data)
        ? (data as Record<string, unknown>)
        : undefined,
  };
};

/** The API's own message for a failure, or `fallback` when it sent none. */
export const apiErrorMessage = (error: unknown, fallback: string): string =>
  apiErrorDetail(error).message ?? fallback;

/**
 * Whether a rejection carries `code`: the demo stores' own error, or the live API's response.
 * Pass API error codes only - an AxiosError's own `code` is axios's (e.g. `ERR_BAD_REQUEST`).
 */
export const hasErrorCode = (error: unknown, code: string): boolean =>
  (error as { code?: unknown } | null)?.code === code ||
  apiErrorDetail(error).code === code;
